import './unifiedUI.scss'
import ReactDOM from 'react-dom/client'
import React, { useState, useEffect, useMemo } from 'react'
import { Canvas } from '@react-three/fiber'
import { SpeedInsights } from '@vercel/speed-insights/react'
import SimpleExperience from './SimpleExperience.jsx'
import { CameraProvider, useCamera } from './SimpleCameraSystem.jsx'
import { MusicPlayerProvider } from './MusicPlayerContext.jsx'
import { HiddenAudioController } from './HiddenAudioController.jsx'
import { OverlayManagerProvider, useOverlayManager } from './overlayManager.jsx'
import { LoadingScreen } from './LoadingScreen.jsx'
import { InvisibleHeader } from './InvisibleHeader.jsx'
import { OverlayIntro } from './overlayIntro.jsx'
import GalleryOverlay from './galleryOverlay.jsx'
import AboutGallery from './aboutGallery.jsx'
import MusicGallery from './musicGallery.jsx'
import ContactGallery from './contactGallery.jsx'

function App() {
    const [experienceStarted, setExperienceStarted] = useState(false)
    const [audioEnabled, setAudioEnabled] = useState(false)
    const [sceneReady, setSceneReady] = useState(false)
    const [imagesReady, setImagesReady] = useState(false)
    const [userMadeChoice, setUserMadeChoice] = useState(false)
    
    // Check for debug mode on mount
    const isDebugMode = useMemo(() => {
        const urlParams = new URLSearchParams(window.location.search)
        const debugParam = urlParams.get('debug')
        
        // Auto-enable debug mode in development (localhost)
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        const autoDebug = isLocalhost && debugParam === null // Auto-enable only if no explicit param
        
        const debugEnabled = debugParam === 'mika-onboarding' || autoDebug
        
        console.log('🔍 Debug mode check:', { 
            url: window.location.href, 
            search: window.location.search,
            debugParam,
            isLocalhost,
            autoDebug,
            isDebugMode: debugEnabled
        })
        
        if (debugEnabled) {
            console.log('🔧 DEBUG MODE ENABLED - Will auto-open work overlay to MIKA onboarding')
        }
        
        return debugEnabled
    }, [])
    
    console.log('App render states:', { experienceStarted, sceneReady, imagesReady, userMadeChoice, isDebugMode })

    const handleStartExperience = (withAudio) => {
        setAudioEnabled(withAudio)
        setUserMadeChoice(true)
        console.log(`User chose audio: ${withAudio}`)
        
        if (sceneReady && imagesReady) {
            setExperienceStarted(true)
            console.log('Experience starting immediately - scene and images ready')
        }
    }

    useEffect(() => {
        if (userMadeChoice && sceneReady && imagesReady && !experienceStarted) {
            setTimeout(() => {
                setExperienceStarted(true)
                console.log('Experience starting - all conditions met')
            }, 100)
        }
    }, [userMadeChoice, sceneReady, imagesReady, experienceStarted])
    
    // Auto-start experience in debug mode
    useEffect(() => {
        if (isDebugMode && sceneReady && imagesReady && !experienceStarted) {
            console.log('🔧 DEBUG MODE: Auto-starting experience')
            setAudioEnabled(false) // Start without audio in debug mode
            setUserMadeChoice(true)
            setExperienceStarted(true)
        }
    }, [isDebugMode, sceneReady, imagesReady, experienceStarted])

    const showLoadingScreen = !experienceStarted

    return (
        <OverlayManagerProvider>
            <MusicPlayerProvider initialAudioEnabled={audioEnabled}>
                <CameraProvider>
                    <AppContent 
                        showLoadingScreen={showLoadingScreen}
                        handleStartExperience={handleStartExperience}
                        sceneReady={sceneReady}
                        imagesReady={imagesReady}
                        userMadeChoice={userMadeChoice}
                        setImagesReady={() => setImagesReady(true)}
                        audioEnabled={audioEnabled}
                        setAudioEnabled={setAudioEnabled}
                        setSceneReady={() => setSceneReady(true)}
                    />
                </CameraProvider>
            </MusicPlayerProvider>
        </OverlayManagerProvider>
    )
}

function AppContent({ 
    showLoadingScreen, 
    handleStartExperience, 
    sceneReady, 
    imagesReady, 
    userMadeChoice,
    setImagesReady,
    audioEnabled,
    setAudioEnabled,
    setSceneReady
}) {
    const { activeOverlay, closingOverlay, closeOverlay, showOverlay } = useOverlayManager()
    const { enterHouse, moveToState } = useCamera()
    
    // Debug mode keyboard shortcut: Shift+D+M to jump to MIKA onboarding
    useEffect(() => {
        let shiftPressed = false
        let dPressed = false
        
        const handleKeyDown = (event) => {
            // Track shift and D keys for debug shortcut
            if (event.key === 'Shift') shiftPressed = true
            if (shiftPressed && event.key.toLowerCase() === 'd') dPressed = true
            if (shiftPressed && dPressed && event.key.toLowerCase() === 'm') {
                event.preventDefault()
                console.log('🔧 DEBUG MODE: Jumping to MIKA onboarding with annotations')
                // Add debug parameter to URL
                const url = new URL(window.location)
                url.searchParams.set('debug', 'mika-onboarding')
                window.history.pushState({}, '', url)
                // Open work overlay
                showOverlay('work')
            }
            
            // F17 key listener to toggle about overlay
            if (event.key === 'F17') {
                event.preventDefault()
                console.log('F17 pressed - toggling about overlay')
                if (activeOverlay === 'about') {
                    closeOverlay()
                } else {
                    showOverlay('about')
                }
            }
        }
        
        const handleKeyUp = (event) => {
            if (event.key === 'Shift') {
                shiftPressed = false
                dPressed = false
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        window.addEventListener('keyup', handleKeyUp)
        return () => {
            window.removeEventListener('keydown', handleKeyDown)
            window.removeEventListener('keyup', handleKeyUp)
        }
    }, [activeOverlay, closeOverlay, showOverlay])
    
    // Handle close - move camera back to outside and close overlay
    const handleClose = () => {
        moveToState('outside') // Move camera back to position 1
        closeOverlay() // Close the overlay
    }
    
    // Handle overlay close - move camera back to parent state
    const handleOverlayClose = (overlayType) => {
        // Map overlays to their parent camera states
        const parentStates = {
            'work': 'lounge',
            'music': 'lounge',
            'about': 'office',
            'contact': 'office'
        }
        
        const parentState = parentStates[overlayType]
        if (parentState) {
            moveToState(parentState)
        }
        closeOverlay()
    }
    
    // Handle enter - enter the house and close overlay
    const handleEnter = () => {
        enterHouse() // Trigger enter house animation and audio
        closeOverlay() // Close the overlay
    }

    return (
        <>
            {/* Overlay Intro */}
            <OverlayIntro 
                isActive={activeOverlay === 'intro'}
                onRequestClose={handleClose}
                onEnter={handleEnter}
            />
            
            {/* Work Gallery Overlay */}
            <GalleryOverlay
                state={activeOverlay === 'work' ? 'open' : 'closed'}
                onRequestClose={() => handleOverlayClose('work')}
                audioEnabled={audioEnabled}
                setAudioEnabled={setAudioEnabled}
            />
            
            {/* About Gallery Overlay */}
            <AboutGallery
                state={activeOverlay === 'about' ? 'open' : 'closed'}
                onRequestClose={() => handleOverlayClose('about')}
            />
            
            {/* Music Gallery Overlay */}
            <MusicGallery
                state={activeOverlay === 'music' ? 'open' : 'closed'}
                onRequestClose={() => handleOverlayClose('music')}
            />
            
            {/* Contact Gallery Overlay */}
            <ContactGallery
                state={activeOverlay === 'contact' ? 'open' : 'closed'}
                onRequestClose={() => handleOverlayClose('contact')}
            />
            
            {showLoadingScreen && (
                <LoadingScreen 
                    onStartExperience={handleStartExperience}
                    sceneReady={sceneReady}
                    imagesReady={imagesReady}
                    userMadeChoice={userMadeChoice}
                    onImagesReady={setImagesReady}
                />
            )}
            
            <Canvas
                camera={ {
                    fov: 45,
                    near: 0.1,
                    far: 2000,
                    position: [ 0, 1.8, 14 ]
                } }
            >
                <SimpleExperience 
                    audioEnabled={audioEnabled} 
                    onSceneReady={setSceneReady}
                />
            </Canvas>
            
            <HiddenAudioController />
            <InvisibleHeader 
                audioEnabled={audioEnabled}
                setAudioEnabled={setAudioEnabled}
            />
            <SpeedInsights />
        </>
    )
}

const root = ReactDOM.createRoot(document.querySelector('#root'))

root.render(<App />)